import { useState, useEffect, useRef } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import Map, { Marker, Popup } from 'react-map-gl';
import { MapPin, Search, Menu, X, Heart, Users, Navigation, MapPinned, Filter } from 'lucide-react';
import 'mapbox-gl/dist/mapbox-gl.css';
import './App.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import AuthModal from './components/AuthModal';
import UserMenu from './components/UserMenu';
import SubmitResourceModal from './components/SubmitResourceModal';
import RegistrationPromo from './components/RegistrationPromo';
import AdminDashboard from './components/AdminDashboard';
import StructuredData from './components/StructuredData';
import GlobalSearch from './components/GlobalSearch';
import ReportInfoModal from './components/ReportInfoModal';

const API_BASE = import.meta.env.VITE_API_URL || '/api';
const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;

function App() {
  const [mode, setMode] = useState('need_help'); // 'need_help' or 'want_help'
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedResource, setSelectedResource] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [showFilters, setShowFilters] = useState(true); // Mobile filter toggle
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mapError, setMapError] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [searchInfo, setSearchInfo] = useState(null);
  const [stats, setStats] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null); // Track current search location
  const [showStats, setShowStats] = useState(false); // For mobile stats toggle
  const [showAuthModal, setShowAuthModal] = useState(false); // Authentication modal
  const [showSubmitModal, setShowSubmitModal] = useState(false); // Resource submission modal
  const [showReportModal, setShowReportModal] = useState(false); // Report issue modal
  const [reportingResource, setReportingResource] = useState(null);

  // SEO Info
  const pageTitle = mode === 'need_help'
    ? 'HumanAid - Find Food, Shelter & Help'
    : 'HumanAid - Volunteer & Give Help';

  const pageDescription = mode === 'need_help'
    ? 'Find local humanitarian resources including food pantries, shelters, and healthcare services.'
    : 'Connect with your community and help those in need by listing resources or volunteering.';

  // Map state
  const [viewState, setViewState] = useState({
    longitude: -89.6501, // Illinois center
    latitude: 40.6331,
    zoom: 7
  });

  // Load categories
  useEffect(() => {
    fetchCategories();
    fetchStats();
  }, [mode]);

  // Ref for Mapbox instance
  const mapRef = useRef(null);

  // Force map resize when filters toggle (to fix grey gap)
  useEffect(() => {
    const startTime = Date.now();
    const duration = 400; // slightly longer than CSS transition

    const animateResize = () => {
      if (mapRef.current) {
        mapRef.current.resize();
      }

      if (Date.now() - startTime < duration) {
        requestAnimationFrame(animateResize);
      }
    };

    // Start resize loop
    requestAnimationFrame(animateResize);

    // Final check
    setTimeout(() => {
      if (mapRef.current) mapRef.current.resize();
    }, duration);

  }, [showFilters]);

  // Load resources when category or location changes
  useEffect(() => {
    fetchResources();
  }, [selectedCategory, currentLocation]);

  // Debug: Log when resources change
  useEffect(() => {
    console.log('[HumanAid] Resources state updated:', {
      total: resources.length,
      withCoords: resources.filter(r => r.latitude && r.longitude).length,
      sample: resources.slice(0, 2).map(r => ({ id: r.id, name: r.name, lat: r.latitude, lon: r.longitude }))
    });
  }, [resources]);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_BASE}/resources?mode=${mode}`);
      if (!response.ok) throw new Error('Failed to fetch stats');

      const data = await response.json();
      // Use total from backend, or fallback to count
      const resourcesList = data.resources || [];
      const totalResources = data.total || data.count || resourcesList.length;
      // Count unique cities from returned resources (sample)
      const uniqueCities = new Set(resourcesList.map(r => `${r.city}, ${r.state}`)).size;
      // Stats removed per user request
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE}/categories?mode=${mode}`);
      if (!response.ok) throw new Error('Failed to fetch categories');
      const data = await response.json();
      setCategories(data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    }
  };

  const fetchResources = async () => {
    setLoading(true);
    // Don't fetch if showing favorites (handled by handleShowFavorites)
    if (selectedCategory === 'favorites') {
      setLoading(false);
      return;
    }

    try {
      let url;
      if (searchQuery) {
        url = `${API_BASE}/resources/search?q=${encodeURIComponent(searchQuery)}&mode=${mode}`;
      } else if (currentLocation) {
        // ... existing location logic ...
        url = `${API_BASE}/resources?lat=${currentLocation.lat}&lon=${currentLocation.lon}&radius=20&mode=${mode}&limit=50`;
        // Add category filter if selected
        if (selectedCategory) {
          url += `&category=${selectedCategory}`;
        }
      } else {
        url = `${API_BASE}/resources?mode=${mode}&limit=100`;
        if (selectedCategory) {
          url += `&category=${selectedCategory}`;
        }
      }

      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch resources');

      const data = await response.json();
      const resourcesList = data.resources || [];

      // If we have stats in the response, update them
      if (data.total !== undefined) {
        // checking if setStats should be updated could go here if stats were used
      }

      setResources(resourcesList);

      if (resourcesList.length === 0) {
        // Don't unmount the map, just show a message
        setMapError(null);
        if (!searchQuery) {
          setSearchInfo('No resources found in this area/category.');
          setTimeout(() => setSearchInfo(null), 4000);
        }
      } else {
        setMapError(null);
      }
    } catch (error) {
      console.error('Error fetching resources:', error);
      setResources([]);
      setMapError('Failed to load resources. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setLoading(true);
    try {
      // Build search URL with location if available
      let searchUrl = `${API_BASE}/search?q=${encodeURIComponent(searchQuery)}`;

      // If we have a current location, include it for location-aware search
      if (currentLocation) {
        searchUrl += `&lat=${currentLocation.lat}&lon=${currentLocation.lon}&radius=${currentLocation.radius || 50}`;
      }

      const response = await fetch(searchUrl);
      const data = await response.json();
      setResources(data.results);

      // Show search mode info
      if (data.results.length > 0) {
        if (data.searchMode === 'local') {
          setSearchInfo(`Found ${data.count} results for "${searchQuery}" nearby`);
        } else if (data.searchMode === 'closest') {
          const closest = data.results[0];
          setSearchInfo(`No "${searchQuery}" nearby. Showing closest: ${closest.city}, ${closest.state} (${closest.distance?.toFixed(1)} miles away)`);
        } else {
          setSearchInfo(`Found ${data.count} results for "${searchQuery}"`);
        }
        setTimeout(() => setSearchInfo(null), 6000);

        // Center map on first result
        setViewState({
          ...viewState,
          longitude: data.results[0].longitude,
          latitude: data.results[0].latitude,
          zoom: data.searchMode === 'local' ? 12 : 10
        });
      } else {
        setSearchInfo(`No results found for "${searchQuery}". Try a different search term.`);
        setTimeout(() => setSearchInfo(null), 5000);
      }
    } catch (error) {
      console.error('Error searching:', error);
    } finally {
      setLoading(false);
    }
  };


  const trackView = async (id) => {
    try {
      await fetch(`${API_BASE}/resources/${id}/view`, { method: 'POST' });
    } catch (err) {
      console.error('Error tracking view:', err);
    }
  };

  // Handle selection from Global Search
  const handleGlobalSelect = (resource) => {
    trackView(resource.id);

    if (resource.latitude && resource.longitude) {
      setViewState({
        ...viewState,
        latitude: resource.latitude,
        longitude: resource.longitude,
        zoom: 15
      });
      setCurrentLocation({
        lat: resource.latitude,
        lon: resource.longitude,
        radius: 20
      });
    }
    setResources([resource]);
    setSearchInfo(`Found: ${resource.name}`);

    // On mobile, scroll to map/resource
    const mapElement = document.querySelector('.map-container');
    if (mapElement) mapElement.scrollIntoView({ behavior: 'smooth' });
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        // Update map view
        setViewState({
          ...viewState,
          longitude,
          latitude,
          zoom: 12
        });

        // Reverse geocode to get place name
        fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?access_token=${MAPBOX_TOKEN}`)
          .then(res => res.json())
          .then(data => {
            const placeName = data.features && data.features[0] ? data.features[0].place_name : 'your location';
            setCurrentLocation({ lat: latitude, lon: longitude, radius: 25, placeName });
          })
          .catch(() => {
            setCurrentLocation({ lat: latitude, lon: longitude, radius: 25, placeName: 'your location' });
          });
        setGettingLocation(false);
      },
      (error) => {
        console.error('Error getting location:', error);
        alert('Unable to get your location. Please enable location services.');
        setGettingLocation(false);
      }
    );
  };

  const searchByZipCode = async (e) => {
    e.preventDefault();
    if (!zipCode.trim()) return;

    setLoading(true);
    try {
      // First, try to geocode the zip code to get coordinates
      const geocodeResponse = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(zipCode)}.json?country=US&types=postcode&access_token=${MAPBOX_TOKEN}`
      );
      const geocodeData = await geocodeResponse.json();

      if (geocodeData.features && geocodeData.features.length > 0) {
        const [longitude, latitude] = geocodeData.features[0].center;
        const placeName = geocodeData.features[0].place_name;

        // Center map on zip code location
        setViewState({
          ...viewState,
          longitude,
          latitude,
          zoom: 11
        });

        // Set current location (this will trigger fetchResources via useEffect)
        setCurrentLocation({ lat: latitude, lon: longitude, radius: 50, placeName });

        // Show info message after resources load
        setTimeout(() => {
          setSearchInfo(`Searching within 50 miles of ${placeName}`);
          setTimeout(() => setSearchInfo(null), 5000);
        }, 500);
      } else {
        alert(`We couldn't find ZIP code "${zipCode}". Please check the ZIP code and try again, or use the "Near Me" button to search by your current location.`);
      }
    } catch (error) {
      console.error('Error searching by zip code:', error);
      alert('Unable to search at this time. Please try using the "Near Me" button or search by city name.');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to calculate distance between two points
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Favorites Logic
  // Favorites Logic
  const { user, favorites, toggleFavorite, isAdmin } = useAuth();

  const handleFavoriteClick = async (e, resource) => {
    e.stopPropagation();
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    await toggleFavorite(resource.id);
  };

  const handleShowFavorites = async () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    // Toggle: If currently viewing favorites, clear it to show all
    if (selectedCategory === 'favorites') {
      setSelectedCategory(null);
      setSearchInfo('Showing all resources');
      setTimeout(() => setSearchInfo(null), 3000);
      return;
    }

    if (favorites.length === 0) {
      setResources([]);
      setSearchInfo("You haven't saved any favorites yet.");
      setTimeout(() => setSearchInfo(null), 4000);
      return;
    }

    setLoading(true);
    try {
      const idsParam = favorites.join(',');
      const response = await fetch(`${API_BASE}/resources?ids=${idsParam}&limit=1000`);
      if (!response.ok) throw new Error('Failed to fetch resources');

      const data = await response.json();
      const allResources = data.resources || [];
      // No need to filter by ID again since backend did it, but good to be safe or just use as is
      const favResources = allResources;
      let finalResources = favResources;

      // Filter by location if set
      if (currentLocation) {
        finalResources = favResources.filter(r => {
          if (!r.latitude || !r.longitude) return false;
          const dist = calculateDistance(
            currentLocation.lat,
            currentLocation.lon,
            r.latitude,
            r.longitude
          );
          // Add distance property to resource for display
          r.distance = dist;
          return dist <= (currentLocation.radius || 50);
        });

        // Sort by distance
        finalResources.sort((a, b) => a.distance - b.distance);

        setSearchInfo(`Showing ${finalResources.length} favorite resources near ${currentLocation.placeName || 'your location'}`);

        // Keep map centered on current location
        setViewState({
          ...viewState,
          latitude: currentLocation.lat,
          longitude: currentLocation.lon,
          zoom: 11
        });
      } else {
        setSearchInfo(`Showing ${finalResources.length} favorite resources`);

        // Auto-center map on favorites (Global View)
        const validResources = finalResources.filter(r => r.latitude && r.longitude);
        if (validResources.length > 0) {
          if (validResources.length === 1) {
            setViewState({
              ...viewState,
              latitude: validResources[0].latitude,
              longitude: validResources[0].longitude,
              zoom: 13
            });
          } else {
            // Calculate average center
            const avgLat = validResources.reduce((sum, r) => sum + r.latitude, 0) / validResources.length;
            const avgLon = validResources.reduce((sum, r) => sum + r.longitude, 0) / validResources.length;

            setViewState({
              ...viewState,
              latitude: avgLat,
              longitude: avgLon,
              zoom: 9
            });
          }
        }
      }

      setResources(finalResources);
      setSelectedCategory('favorites'); // Special category
    } catch (error) {
      console.error('Error fetching favorites:', error);
      setMapError('Failed to load favorites.');
    } finally {
      setLoading(false);
    }
  };

  // Simple Routing for Admin
  if (mode === 'admin') {
    return (
      <div className="app-container">
        <header className="header">
          <div className="header-content">
            <div className="logo" onClick={() => setMode('need_help')}>
              <Heart className="logo-icon" />
              <h1>HumanAid</h1>
            </div>
            <button className="mode-btn" onClick={() => setMode('need_help')}>
              Back to Map
            </button>
          </div>
        </header>
        <AdminDashboard />
      </div>
    );
  }

  return (
    <div className="app-container">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
      </Helmet>

      <StructuredData resources={resources} />

      {/* Header */}
      <header className="header">
        <div className="header-content">
          <div className="logo">
            <Heart className="logo-icon" />
            <h1>HumanAid</h1>
          </div>

          {/* Mode Toggle */}
          <div className="mode-toggle">
            <button
              className={`mode-btn ${mode === 'need_help' ? 'active' : ''}`}
              onClick={() => setMode('need_help')}
            >
              <Users size={20} />
              <span>I Need Help</span>
            </button>
            <button
              className={`mode-btn ${mode === 'want_help' ? 'active' : ''}`}
              onClick={() => setMode('want_help')}
            >
              <Heart size={20} />
              <span>I Want to Help</span>
            </button>
          </div>

          <div className="header-actions">
            <UserMenu
              onLoginClick={() => setShowAuthModal(true)}
              onSubmitClick={() => setShowSubmitModal(true)}
              onFavoritesClick={handleShowFavorites}
              isFavoritesView={selectedCategory === 'favorites'}
              onAdminClick={() => setMode('admin')}
            />
            <button className="menu-btn" onClick={() => setMenuOpen(!menuOpen)}>
              {menuOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>

        {/* Mobile Global Search - Visible on Mobile Only via CSS */}
        {/* Mobile Global Search - Visible on Mobile Only via CSS */}
        <div className="mobile-global-search">
          <div style={{ flex: 1 }}>
            <GlobalSearch onResourceSelect={handleGlobalSelect} />
          </div>
          <button
            className={`filter-toggle-btn ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
            style={{
              background: showFilters ? '#e0f2fe' : 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              padding: '0.75rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#3b82f6',
              height: '46px',
              width: '46px',
              cursor: 'pointer'
            }}
          >
            <Filter size={20} />
          </button>
        </div>

        {/* Search Bar */}
        <div className={`search-container ${!showFilters ? 'mobile-hidden' : ''}`}>
          <form onSubmit={handleSearch} className="search-bar">
            <Search className="search-icon" size={20} />
            <input
              type="text"
              className="search-input"
              placeholder={mode === 'need_help' ?
                'Search for food, shelter, healthcare...' :
                'Search volunteer opportunities...'
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button type="submit">Search</button>
          </form>

          <form onSubmit={searchByZipCode} className="zip-form">
            <input
              type="text"
              className="zip-input"
              placeholder="Enter ZIP code"
              value={zipCode}
              onChange={(e) => setZipCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
              maxLength="5"
            />
            <button type="submit" className="zip-btn">Go</button>
          </form>

          <button
            type="button"
            className="location-btn"
            onClick={useMyLocation}
            disabled={gettingLocation}
            title="Use my location"
          >
            <Navigation size={20} />
            {gettingLocation ? 'Getting...' : 'Near Me'}
          </button>

          {/* Mobile Category Dropdown */}
          <select
            className="category-dropdown"
            value={selectedCategory || ''}
            onChange={(e) => setSelectedCategory(e.target.value || null)}
          >
            <option value="">All Categories</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.slug}>
                {cat.icon} {cat.name}
              </option>
            ))}
          </select>
        </div>
      </header>

      <div className="main-content">
        {/* Stats Banner Removed */}

        {/* Search Info Banner */}
        {searchInfo && (
          <div className="search-info-banner">
            <p>{searchInfo}</p>
            <button onClick={() => setSearchInfo(null)}>×</button>
          </div>
        )}

        {/* Content Grid - Sidebar, Map, Resource List */}
        <div className="content-grid">
          {/* Sidebar */}
          <aside className={`sidebar ${menuOpen ? 'open' : ''}`}>
            <h2>Categories</h2>
            <div className="category-list">
              <button
                className={`category-item ${!selectedCategory ? 'active' : ''}`}
                onClick={() => setSelectedCategory(null)}
              >
                All Categories
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  className={`category-item ${selectedCategory === cat.slug ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(cat.slug)}
                >
                  {cat.name}
                </button>
              ))}
            </div>
            <RegistrationPromo onSignInClick={() => setShowAuthModal(true)} />
          </aside>

          {/* Map */}
          <div className={`map-container ${!showFilters ? 'expanded' : ''}`}>
            {MAPBOX_TOKEN && !mapError ? (
              <Map
                ref={mapRef}
                {...viewState}
                onMove={evt => setViewState(evt.viewState)}
                mapStyle="mapbox://styles/mapbox/streets-v12"
                mapboxAccessToken={MAPBOX_TOKEN}
                style={{ width: '100%', height: '100%' }}
                onError={(e) => {
                  console.error('Map error:', e);
                  setMapError(e.error?.message || 'Map failed to load');
                }}
                reuseMaps
              >
                {resources.map((resource) => (
                  resource.latitude && resource.longitude && (
                    <Marker
                      key={resource.id}
                      longitude={resource.longitude}
                      latitude={resource.latitude}
                      onClick={(e) => {
                        e.originalEvent.stopPropagation();
                        trackView(resource.id);
                        setSelectedResource(resource);
                      }}
                    >
                      <div className="custom-marker">
                        <div className="marker-pin"></div>
                        <div className="marker-shadow"></div>
                      </div>
                    </Marker>
                  )
                ))}

                {selectedResource && (
                  <Popup
                    longitude={selectedResource.longitude}
                    latitude={selectedResource.latitude}
                    onClose={() => setSelectedResource(null)}
                    closeButton={true}
                    closeOnClick={false}
                  >
                    <div className="popup-content">
                      <h3>{selectedResource.name}</h3>
                      <p>{selectedResource.address}</p>
                      <p>{selectedResource.city}, {selectedResource.state} {selectedResource.zip_code}</p>
                      {selectedResource.phone && (
                        <p><strong>Phone:</strong> {selectedResource.phone}</p>
                      )}
                      <button
                        className={`favorite-btn-popup ${favorites.includes(selectedResource.id) ? 'active' : ''}`}
                        onClick={(e) => handleFavoriteClick(e, selectedResource)}
                      >
                        <Heart size={16} fill={favorites.includes(selectedResource.id) ? "currentColor" : "none"} />
                        {favorites.includes(selectedResource.id) ? 'Saved' : 'Save'}
                      </button>
                      <div className="popup-actions">
                        {/* Get Directions Button */}
                        <a
                          href={`https://www.google.com/maps/dir/?api=1&destination=${selectedResource.latitude},${selectedResource.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="directions-btn"
                        >
                          <MapPinned size={16} />
                          Get Directions
                        </a>
                        {selectedResource.website && (
                          <a
                            href={selectedResource.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="website-btn"
                          >
                            Visit Website
                          </a>
                        )}
                        <button
                          className="report-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setReportingResource(selectedResource);
                            setShowReportModal(true);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#6B7280',
                            fontSize: '0.75rem',
                            cursor: 'pointer',
                            marginTop: '0.5rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          <small>🚩 Report Issue</small>
                        </button>
                      </div>
                    </div>
                  </Popup>
                )}
              </Map>
            ) : (
              <div className="map-placeholder">
                {mapError ? (
                  <>
                    <p>⚠️ Map Error</p>
                    <p>{mapError}</p>
                    <p style={{ fontSize: '0.875rem', marginTop: '1rem' }}>
                      Try refreshing the page or check browser WebGL support
                    </p>
                  </>
                ) : !MAPBOX_TOKEN ? (
                  <>
                    <p>Map requires Mapbox token</p>
                    <p>Set VITE_MAPBOX_TOKEN in .env file</p>
                  </>
                ) : null}
              </div>
            )}

            {loading && (
              <div className="loading-overlay">
                <div className="spinner"></div>
                <p>Loading resources...</p>
              </div>
            )}
          </div>

          {/* Resource List */}
          <div className="resource-list">
            <div className="resource-list-header">
              <h2>
                {selectedCategory
                  ? `${categories.find(c => c.slug === selectedCategory)?.name || 'Resources'}`
                  : 'All Resources'
                }
                {currentLocation && currentLocation.placeName && (
                  <span className="location-context"> near {currentLocation.placeName.split(',')[0]}</span>
                )}
                <span className="count">({resources.length})</span>
              </h2>
              {currentLocation && (
                <button className="clear-location-btn" onClick={() => setCurrentLocation(null)}>
                  Clear Location
                </button>
              )}
            </div>

            <div className="resources">
              {resources.map((resource) => (
                <div
                  key={resource.id}
                  className="resource-card"
                  onClick={() => {
                    setSelectedResource(resource);
                    if (resource.latitude && resource.longitude) {
                      setViewState({
                        ...viewState,
                        longitude: resource.longitude,
                        latitude: resource.latitude,
                        zoom: 14
                      });
                    }
                  }}
                >
                  <h3>{resource.name}</h3>
                  <p className="address">
                    {resource.address}<br />
                    {resource.city}, {resource.state} {resource.zip_code}
                  </p>
                  {resource.phone && (
                    <p className="phone">📞 {resource.phone}</p>
                  )}
                  <div className="resource-links">
                    {resource.latitude && resource.longitude && (
                      <a
                        href={`https://www.google.com/maps/dir/?api=1&destination=${resource.latitude},${resource.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="directions-link"
                        onClick={(e) => e.stopPropagation()}
                      >
                        🗺️ Directions
                      </a>
                    )}
                    <button
                      className={`favorite-btn ${favorites.includes(resource.id) ? 'active' : ''}`}
                      onClick={(e) => handleFavoriteClick(e, resource)}
                      title={favorites.includes(resource.id) ? "Remove from favorites" : "Add to favorites"}
                    >
                      <Heart size={20} fill={favorites.includes(resource.id) ? "currentColor" : "none"} />
                    </button>
                    {resource.website && (
                      <a
                        href={resource.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="website-link"
                        onClick={(e) => e.stopPropagation()}
                      >
                        🌐 Website
                      </a>
                    )}
                  </div>
                  {resource.distance !== undefined && (
                    <p className="distance">
                      📍 {resource.distance < 1
                        ? `${(resource.distance * 5280).toFixed(0)} feet away`
                        : `${resource.distance.toFixed(1)} miles away`
                      }
                    </p>
                  )}
                  {resource.categories && (
                    <div className="tags">
                      {resource.categories.map((cat, idx) => (
                        <span key={idx} className="tag">{cat}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Authentication Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />

      {/* Resource Submission Modal */}
      <SubmitResourceModal
        isOpen={showSubmitModal}
        onClose={() => setShowSubmitModal(false)}
        categories={categories}
      />

      <ReportInfoModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        resource={reportingResource}
      />


    </div>
  );
}

// Wrap App with AuthProvider
function AppWithAuth() {
  return (
    <AuthProvider>
      <HelmetProvider>
        <App />
      </HelmetProvider>
    </AuthProvider>
  );
}

export default AppWithAuth;
